import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { Alert } from "./Alert";

export type Severity = "error" | "warning" | "info" | "success";
export type NotifyFn = (message: string, severity?: Severity) => void;

// Provider 外で呼ばれても落ちないよう no-op を既定値にする
const NotificationContext = createContext<NotifyFn>(() => {});

/**
 * アプリ全体で使う通知(スナックバー)を表示するための共通フック。
 * 旧来の `alert()` の置き換え。`const notify = useNotify(); notify("メッセージ", "error")`
 */
export const useNotify = () => useContext(NotificationContext);

type State = { open: boolean; message: string; severity: Severity };

/**
 * 共通通知プロバイダ。ルート付近に1つ置き、配下から `useNotify()` で呼び出す。
 * 既存の Snackbar ベースの `Alert` を再利用し、一定時間で自動的に閉じる。
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<State>({
    open: false,
    message: "",
    severity: "error",
  });

  const notify = useCallback<NotifyFn>((message, severity = "error") => {
    setState({ open: true, message, severity });
  }, []);

  const close = useCallback(
    () => setState((prev) => ({ ...prev, open: false })),
    []
  );

  // 表示後、一定時間で自動的に閉じる
  useEffect(() => {
    if (!state.open) return;
    const timer = setTimeout(close, 6000);
    return () => clearTimeout(timer);
  }, [state.open, state.message, close]);

  return (
    <NotificationContext.Provider value={notify}>
      {children}
      <Alert
        open={state.open}
        onClose={close}
        message={state.message}
        severity={state.severity}
      />
    </NotificationContext.Provider>
  );
};
